import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  ExternalLink,
  FileClock,
  FileSearch,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'
import {
  cancelCheckRun,
  downloadCheckRunReportPdf,
  fetchCheckRunProgressLog,
  type CheckRunDetailDto,
} from '../services/api'
import {
  createComplianceReviewDecision,
  downloadComplianceAuditorReport,
  downloadComplianceRunDocx,
  downloadEvidenceContent,
  fetchComplianceRunComparisons,
  fetchComplianceRunEvidence,
  fetchComplianceRunExtractions,
  fetchComplianceRunRuleResults,
  fetchComplianceRunTargets,
  fetchComplianceRunWithLegacyFallback,
  type ComplianceComparisonDto,
  type ComplianceExtractionDto,
  type ComplianceRunDetailDto,
  type ComplianceRunResponse,
  type ComplianceTargetRunDto,
  type EvidenceMetadataDto,
} from '../services/complianceV2'
import { EmptyState, KpiCard, MimCard, MimPage, StatusPill, formatDateTime } from '../components/MimUi'
import RunProgressStepper from '../components/RunProgressStepper'
import {
  TARGET_SECTION_FILTER_CHIPS,
  targetMatchesSection,
  targetSectionFilterLabel,
  targetSectionLabel,
  type TargetSectionCode,
} from '../utils/targetSection'
import './CheckRunDetail.css'

type RunTab = 'overview' | 'targets' | 'capture' | 'extractions' | 'comparisons' | 'rules' | 'evidence'

const tabs: Array<{ id: RunTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'targets', label: 'Targets' },
  { id: 'capture', label: 'Captured Page' },
  { id: 'extractions', label: 'Extracted Fields' },
  { id: 'comparisons', label: 'PCR Comparison' },
  { id: 'rules', label: 'Policy Rules' },
  { id: 'evidence', label: 'Evidence / Audit' },
]

const activeStatuses = new Set([
  'Queued',
  'Acquiring',
  'Extracting',
  'Comparing',
  'EvaluatingRules',
  'InProgress',
  'Pending',
  'CancelRequested',
])

export default function CheckRunDetail() {
  const { checkRunId = '' } = useParams()
  const [activeTab, setActiveTab] = useState<RunTab>('overview')

  const runQuery = useQuery({
    queryKey: ['compliance-run-detail', checkRunId],
    queryFn: () => fetchComplianceRunWithLegacyFallback(checkRunId),
    enabled: !!checkRunId,
    retry: false,
    refetchInterval: (query) => {
      const response = query.state.data as ComplianceRunResponse | undefined
      const status = response?.run.status
      return status && activeStatuses.has(status) ? 2500 : false
    },
  })

  if (runQuery.isLoading) {
    return (
      <MimPage title="Compliance Run" subtitle={`Run ${checkRunId}`}>
        <MimCard><EmptyState icon={<LoaderCircle className="run-spin" size={34} />} title="Loading the immutable run ledger" /></MimCard>
      </MimPage>
    )
  }

  if (runQuery.isError || !runQuery.data) {
    return (
      <MimPage title="Compliance Run" subtitle={`Run ${checkRunId}`}>
        <MimCard>
          <div className="run-load-error">
            <AlertTriangle size={32} />
            <div><strong>Run data could not be loaded</strong><span>{apiError(runQuery.error)}</span></div>
            <button type="button" className="mim-btn mim-btn-secondary" onClick={() => runQuery.refetch()}><RefreshCw size={14} /> Retry</button>
          </div>
        </MimCard>
      </MimPage>
    )
  }

  if (runQuery.data.kind === 'legacy') {
    return <LegacyRunDetail run={runQuery.data.run} checkRunId={checkRunId} />
  }

  return (
    <V2RunDetail
      run={runQuery.data.run}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  )
}

function V2RunDetail({
  run,
  activeTab,
  setActiveTab,
}: {
  run: ComplianceRunDetailDto
  activeTab: RunTab
  setActiveTab: (tab: RunTab) => void
}) {
  const queryClient = useQueryClient()
  const [selectedTargetRunId, setSelectedTargetRunId] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [comparisonSectionFilter, setComparisonSectionFilter] = useState<TargetSectionCode | 'All'>('All')
  const cancelMutation = useMutation({
    mutationFn: () => cancelCheckRun(run.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-run-detail', run.id] })
      void queryClient.invalidateQueries({ queryKey: ['compliance-run-targets', run.id] })
      void queryClient.invalidateQueries({ queryKey: ['check-runs'] })
    },
  })
  const targetsQuery = useQuery({
    queryKey: ['compliance-run-targets', run.id],
    queryFn: () => fetchComplianceRunTargets(run.id),
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })
  const targets = targetsQuery.data ?? []

  useEffect(() => {
    setSelectedTargetRunId((current) => targets.some((target) => target.id === current) ? current : targets[0]?.id ?? '')
  }, [targets])

  const selectedTarget = targets.find((target) => target.id === selectedTargetRunId)

  useEffect(() => {
    if (comparisonSectionFilter === 'All') return
    const match = targets.find((target) => targetMatchesSection(target.section, comparisonSectionFilter))
    if (match) setSelectedTargetRunId(match.id)
  }, [comparisonSectionFilter, targets])

  const exportAuditorReport = async () => {
    setExportError(null)
    setExporting(true)
    try {
      const blob = await downloadComplianceAuditorReport(run.id, 'md')
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `compliance-run-${run.id}.md`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (error) {
      setExportError(apiError(error))
    } finally {
      setExporting(false)
    }
  }

  const exportDocxReport = async () => {
    setExportError(null)
    setExporting(true)
    try {
      const blob = await downloadComplianceRunDocx(run.id)
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `compliance-run-${run.id}.docx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (error) {
      setExportError(apiError(error))
    } finally {
      setExporting(false)
    }
  }

  const extractionsQuery = useQuery({
    queryKey: ['compliance-run-extractions', run.id, selectedTargetRunId],
    queryFn: () => fetchComplianceRunExtractions(run.id, selectedTargetRunId),
    enabled: !!selectedTargetRunId,
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })
  const comparisonsQuery = useQuery({
    queryKey: ['compliance-run-comparisons', run.id, selectedTargetRunId],
    queryFn: () => fetchComplianceRunComparisons(run.id, selectedTargetRunId),
    enabled: !!selectedTargetRunId,
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })
  // Policy rules default to ALL targets: per-target views hid e.g. the Technical page's lambda
  // failure while the user was looking at the Marketing target, making real findings look missed.
  const [rulesTargetRunId, setRulesTargetRunId] = useState(ALL_TARGETS)
  const rulesQuery = useQuery({
    queryKey: ['compliance-run-rule-results', run.id, rulesTargetRunId, targets.map((t) => t.id).join(',')],
    queryFn: async () => {
      if (rulesTargetRunId !== ALL_TARGETS) return fetchComplianceRunRuleResults(run.id, rulesTargetRunId)
      const perTarget = await Promise.all(targets.map((target) => fetchComplianceRunRuleResults(run.id, target.id)))
      return perTarget.flat()
    },
    enabled: rulesTargetRunId === ALL_TARGETS ? targets.length > 0 : !!rulesTargetRunId,
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })
  const evidenceQuery = useQuery({
    queryKey: ['compliance-run-evidence', run.id],
    queryFn: () => fetchComplianceRunEvidence(run.id),
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })

  const counts = run.counts ?? {
    targets: targets.length,
    processedTargets: targets.filter((target) => target.outcome).length,
    findings: targets.reduce((total, target) => total + target.findingCount, 0),
    matches: 0,
    mismatches: 0,
    needsReview: 0,
    technicalErrors: 0,
    missingObserved: 0,
  }

  return (
    <MimPage
      title="Auditable Compliance Run"
      subtitle={`${run.productName || run.productCode || 'Product'} · ${run.id}`}
      actions={
        <div className="mim-toolbar">
          <Link className="mim-btn mim-btn-secondary" to="/check-runs"><ArrowLeft size={14} /> History</Link>
          {activeStatuses.has(run.status) && (
            <button
              type="button"
              className="mim-btn mim-btn-danger"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              {cancelMutation.isPending ? 'Stopping…' : 'Stop run'}
            </button>
          )}
          <button type="button" className="mim-btn mim-btn-secondary" disabled={exporting} onClick={exportAuditorReport}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Auditor report (.md)'}
          </button>
          <button type="button" className="mim-btn mim-btn-secondary" disabled={exporting} onClick={exportDocxReport}>
            <Download size={14} /> Spot-check report (.docx)
          </button>
          <StatusPill status={run.outcome || run.status} />
        </div>
      }
    >
      {exportError && <div className="run-row-error" style={{ marginBottom: 12 }}>{exportError}</div>}
      <div className="mim-stack">
        <div className={`run-assurance-banner ${run.isReplayable === true ? '' : 'warning'}`}>
          {run.isReplayable === true ? <ShieldCheck size={24} /> : <AlertTriangle size={24} />}
          <div>
            <strong>{run.isReplayable === true ? 'Immutable evidence snapshot' : 'Replay validation incomplete'}</strong>
            <span>{run.isReplayable === true
              ? 'PCR values, configuration versions, website captures, decisions, and hashes are fixed to this run.'
              : 'One or more frozen inputs are unavailable or unverified. This run must not be treated as a final audited decision.'}</span>
          </div>
        </div>

        <MimCard title="Run progress" subtitle="Pipeline stages persist per target; the stepper reflects the slowest active target.">
          <RunProgressStepper
            status={run.outcome || run.status}
            startedUtc={run.startedUtc}
            createdUtc={run.createdUtc}
            completedUtc={run.completedUtc}
            targetStages={targets.map((target) => target.stage)}
          />
        </MimCard>

        <div className="mim-grid-4">
          <KpiCard label="Targets" value={counts.targets} detail={`${counts.processedTargets} processed`} />
          <KpiCard label="Matches" value={counts.matches} tone="green" />
          <KpiCard label="Mismatches" value={counts.mismatches} tone="red" />
          <KpiCard label="Needs review" value={counts.needsReview + counts.technicalErrors} tone="orange" detail={counts.technicalErrors ? `${counts.technicalErrors} technical errors` : undefined} />
        </div>

        <nav className="run-tabs" role="tablist" aria-label="Compliance run details">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === 'overview' && <OverviewTab run={run} targets={targets} targetsLoading={targetsQuery.isLoading} targetsError={targetsQuery.error} />}
        {activeTab === 'targets' && <TargetsTab targets={targets} loading={targetsQuery.isLoading} error={targetsQuery.error} />}
        {activeTab === 'capture' && (
          <RunDataTab title="Captured Page" subtitle="The raw scraped content as the pipeline saw it, with every PCR field allocation highlighted in place.">
            <TargetPicker targets={targets} value={selectedTargetRunId} onChange={setSelectedTargetRunId} />
            <CaptureExplorer
              evidence={(evidenceQuery.data ?? []).filter((artifact) => artifact.targetRunId === selectedTargetRunId)}
              extractions={extractionsQuery.data ?? []}
              loading={evidenceQuery.isLoading || extractionsQuery.isLoading}
            />
          </RunDataTab>
        )}
        {activeTab === 'extractions' && (
          <RunDataTab title="Extracted Fields" subtitle="Accepted and rejected candidates remain visible with the exact capture and quote used.">
            <TargetPicker targets={targets} value={selectedTargetRunId} onChange={setSelectedTargetRunId} />
            {extractionsQuery.isError
              ? <QueryFailure label="Extracted fields could not be loaded" error={extractionsQuery.error} retry={() => extractionsQuery.refetch()} />
              : <ExtractionsTable rows={extractionsQuery.data ?? []} loading={extractionsQuery.isLoading} />}
          </RunDataTab>
        )}
        {activeTab === 'comparisons' && (
          <RunDataTab title="PCR Comparison" subtitle="Every monitored PCR field has an explicit outcome; missing and technical states are never shown as passes.">
            <div className="run-section-filters" role="group" aria-label="Filter comparisons by dossier section">
              {TARGET_SECTION_FILTER_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className={comparisonSectionFilter === chip ? 'active' : ''}
                  onClick={() => setComparisonSectionFilter(chip)}
                >
                  {targetSectionFilterLabel(chip)}
                </button>
              ))}
            </div>
            <TargetPicker targets={targets} value={selectedTargetRunId} onChange={setSelectedTargetRunId} />
            {comparisonsQuery.isError
              ? <QueryFailure label="PCR comparison results could not be loaded" error={comparisonsQuery.error} retry={() => comparisonsQuery.refetch()} />
              : <ComparisonsPanel rows={comparisonsQuery.data ?? []} runId={run.id} targetRunId={selectedTargetRunId} loading={comparisonsQuery.isLoading} />}
          </RunDataTab>
        )}
        {activeTab === 'rules' && (
          <RunDataTab title="Policy Rules" subtitle="Typed policy results are scoped to this product and frozen PCR revision. Defaults to every website in this run.">
            <TargetPicker targets={targets} value={rulesTargetRunId} onChange={setRulesTargetRunId} allowAll />
            {rulesQuery.isError
              ? <QueryFailure label="Policy rule results could not be loaded" error={rulesQuery.error} retry={() => rulesQuery.refetch()} />
              : <RuleResultsTable rows={rulesQuery.data ?? []} loading={rulesQuery.isLoading} showSource={rulesTargetRunId === ALL_TARGETS} />}
          </RunDataTab>
        )}
        {activeTab === 'evidence' && (
          <EvidenceAuditTab run={run} target={selectedTarget} evidence={evidenceQuery.data ?? []} loading={evidenceQuery.isLoading} error={evidenceQuery.error} retry={() => evidenceQuery.refetch()} />
        )}
      </div>
    </MimPage>
  )
}

type CaptureSegment = { text: string; field?: string; accepted?: boolean; first?: boolean }

function buildCaptureSegments(content: string, extractions: ComplianceExtractionDto[], fieldFilter: string): CaptureSegment[] {
  const lower = content.toLowerCase()
  const marks: Array<{ start: number; end: number; field: string; accepted: boolean }> = []
  const relevant = extractions.filter((row) =>
    row.evidenceQuote && (fieldFilter === 'All' || row.canonicalField === fieldFilter))
  for (const row of relevant) {
    const quote = row.evidenceQuote!.trim()
    // The AI often quotes "Label | Value" even when other table cells sit between them in the raw
    // markdown, so fall back to highlighting each pipe-separated fragment individually.
    const parts = [quote, ...(quote.includes('|') ? quote.split('|').map((p) => p.trim()) : [])]
      .filter((p) => p.length >= 3)
    for (const part of parts) {
      // Highlight EVERY occurrence (bounded): the same cell value repeats across table rows and
      // first-match-only made correct extractions look skipped in later rows.
      const needle = part.toLowerCase()
      let from = 0
      let found = false
      for (let occurrence = 0; occurrence < 40; occurrence++) {
        const idx = lower.indexOf(needle, from)
        if (idx < 0) break
        marks.push({ start: idx, end: idx + part.length, field: row.canonicalField, accepted: row.disposition === 'Accepted' })
        from = idx + part.length
        found = true
      }
      if (found && part === quote) break
    }
  }
  marks.sort((a, b) => a.start - b.start || b.end - a.end)
  const segments: CaptureSegment[] = []
  const seenFields = new Set<string>()
  let cursor = 0
  for (const mark of marks) {
    if (mark.start < cursor) continue
    if (mark.start > cursor) segments.push({ text: content.slice(cursor, mark.start) })
    const first = !seenFields.has(mark.field)
    seenFields.add(mark.field)
    segments.push({ text: content.slice(mark.start, mark.end), field: mark.field, accepted: mark.accepted, first })
    cursor = mark.end
  }
  if (cursor < content.length) segments.push({ text: content.slice(cursor) })
  return segments
}

function CaptureExplorer({ evidence, extractions, loading }: {
  evidence: EvidenceMetadataDto[]
  extractions: ComplianceExtractionDto[]
  loading: boolean
}) {
  const markdownArtifact = evidence.find((artifact) => artifact.artifactType === 'Markdown')
  const [content, setContent] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [fieldFilter, setFieldFilter] = useState<string>('All')

  useEffect(() => {
    let cancelled = false
    setContent(null)
    setLoadError(null)
    if (!markdownArtifact) return
    downloadEvidenceContent(markdownArtifact.artifactId)
      .then((blob) => blob.text())
      .then((text) => { if (!cancelled) setContent(text) })
      .catch((error) => { if (!cancelled) setLoadError(apiError(error)) })
    return () => { cancelled = true }
  }, [markdownArtifact?.artifactId])

  const fieldSummaries = Object.values(
    extractions.reduce<Record<string, { field: string; label: string; accepted: number; rejected: number; notFound: number; values: string[] }>>(
      (acc, row) => {
        const entry = acc[row.canonicalField] ?? {
          field: row.canonicalField,
          label: row.displayLabel || row.canonicalField,
          accepted: 0,
          rejected: 0,
          notFound: 0,
          values: [],
        }
        const hasValue = !!(row.normalizedValue || row.rawValue)
        if (!hasValue) {
          // "Rejected" without a value just means nothing was on the page - a different signal
          // to reviewers than data that was found but deliberately not used.
          entry.notFound++
        } else if (row.disposition === 'Accepted') {
          entry.accepted++
          const value = row.normalizedValue || row.rawValue
          if (value && !entry.values.includes(value) && entry.values.length < 6) entry.values.push(value)
        } else {
          entry.rejected++
        }
        acc[row.canonicalField] = entry
        return acc
      },
      {},
    ),
  ).sort((a, b) => a.label.localeCompare(b.label))

  const jumpToField = (field: string) => {
    setFieldFilter(field)
    // The mark for this field re-renders after the filter applies.
    window.setTimeout(() => {
      document.getElementById(`capture-mark-${field}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
  }

  if (loading) return <EmptyState icon={<LoaderCircle className="run-spin" size={30} />} title="Loading capture and extraction data" />
  if (!markdownArtifact) return <EmptyState icon={<FileSearch size={30} />} title="No markdown capture recorded for this target" />
  if (loadError) return <div className="run-row-error">{loadError}</div>
  if (content === null) return <EmptyState icon={<LoaderCircle className="run-spin" size={30} />} title="Downloading captured page content" />

  const segments = buildCaptureSegments(content, extractions, fieldFilter)

  return (
    <div className="capture-explorer">
      <aside className="capture-fields">
        <div className="capture-fields-header">
          <strong>PCR field allocation</strong>
          <span>{fieldSummaries.length} fields · click to locate in the capture</span>
        </div>
        <button
          type="button"
          className={`capture-field-row ${fieldFilter === 'All' ? 'active' : ''}`}
          onClick={() => setFieldFilter('All')}
        >
          <span className="capture-field-name">Show all fields</span>
        </button>
        {fieldSummaries.map((summary) => (
          <button
            key={summary.field}
            type="button"
            className={`capture-field-row ${fieldFilter === summary.field ? 'active' : ''}`}
            onClick={() => jumpToField(summary.field)}
          >
            <span className="capture-field-name">{summary.label}</span>
            <span className="capture-field-meta">
              {summary.accepted > 0 && <span className="capture-chip accepted">{summary.accepted} accepted</span>}
              {summary.rejected > 0 && <span className="capture-chip rejected">{summary.rejected} rejected</span>}
              {summary.notFound > 0 && <span className="capture-chip notfound">nothing found</span>}
              {summary.accepted === 0 && summary.rejected === 0 && summary.notFound === 0 && <span className="capture-chip none">not checked</span>}
            </span>
            {summary.values.length > 0 && (
              <span className="capture-field-values">{summary.values.join(' · ')}</span>
            )}
          </button>
        ))}
      </aside>
      <div className="capture-doc-wrap">
        <div className="capture-doc-legend">
          <span><mark className="capture-mark accepted">accepted</mark> value used for comparison</span>
          <span><mark className="capture-mark rejected">rejected</mark> data found but not used</span>
          <span><span className="capture-chip notfound">nothing found</span> no data for this field on the page</span>
        </div>
        <pre className="capture-doc">
          {segments.map((segment, index) =>
            segment.field ? (
              <mark
                key={index}
                id={segment.first ? `capture-mark-${segment.field}` : undefined}
                className={`capture-mark ${segment.accepted ? 'accepted' : 'rejected'}`}
                title={segment.field}
              >
                {segment.text}
              </mark>
            ) : (
              <span key={index}>{segment.text}</span>
            ))}
        </pre>
      </div>
    </div>
  )
}

function OverviewTab({ run, targets, targetsLoading, targetsError }: { run: ComplianceRunDetailDto; targets: ComplianceTargetRunDto[]; targetsLoading: boolean; targetsError: unknown }) {
  return (
    <div className="run-overview-grid">
      <MimCard title="Frozen Run Inputs" subtitle="These revisions are the basis of every result in this run.">
        <dl className="run-definition-list">
          <Definition label="Product" value={`${run.productName || '-'}${run.productCode ? ` (${run.productCode})` : ''}`} />
          <Definition label="Variant" value={run.productVariantName || run.productVariantId || 'All / not specified'} />
          <Definition label="PCR revision" value={run.sourceOfTruth?.version || run.sourceOfTruthVersion || run.sourceOfTruthRecordId} mono />
          <Definition label="PCR hash" value={run.sourceOfTruth?.hash || run.sourceOfTruthHash || 'Hash unavailable'} mono />
          <Definition label="Variant coverage" value={variantCoverageLabel(run.coveredVariants)} />
          {run.fieldCoverage && <Definition label="Field coverage" value={`${run.fieldCoverage.monitoredClaims} claims monitored · ${run.fieldCoverage.outOfScopeRegisterFields} register fields out of scope · ${run.fieldCoverage.prohibitedLanguageRules} language rules`} />}
          <Definition label="Extraction profile" value={revisionLabel(run.extractionProfile)} mono />
          <Definition label="Extraction profile scope" value={run.extractionProfileMatchedScope || 'Not reported - profile may have been re-scoped since this run'} />
          <Definition label="Rule pack" value={revisionLabel(run.rulePack, 'Core comparison only')} mono />
          <Definition label="Prompt pack" value={revisionLabel(run.promptPack, 'Not used')} mono />
          <Definition label="Algorithm" value={run.comparisonAlgorithmVersion || 'Version not reported'} mono />
          <Definition label="AI deployment" value={[run.aiDeployment, run.aiModelVersion].filter(Boolean).join(' / ') || 'Not used'} mono />
        </dl>
      </MimCard>
      <MimCard title="Run Timeline">
        <dl className="run-definition-list">
          <Definition label="Status" value={<StatusPill status={run.outcome || run.status} />} />
          <Definition label="Created" value={formatDateTime(run.createdUtc)} />
          <Definition label="Started" value={formatDateTime(run.startedUtc)} />
          <Definition label="Completed" value={formatDateTime(run.completedUtc)} />
          <Definition label="Initiated by" value={run.initiatedBy || 'Authenticated actor not reported'} />
          <Definition label="Application build" value={run.applicationVersion || 'Not reported'} mono />
          <Definition label="Reason" value={run.reason || 'Website compliance check'} />
        </dl>
      </MimCard>
      <MimCard title="Target Pipeline" subtitle="Each website progresses through one persisted, idempotent pipeline." className="run-overview-wide">
        {targetsError
          ? <QueryFailure label="Target pipeline could not be loaded" error={targetsError} />
          : <TargetsTable targets={targets} loading={targetsLoading} compact />}
      </MimCard>
    </div>
  )
}

function TargetsTab({ targets, loading, error }: { targets: ComplianceTargetRunDto[]; loading: boolean; error: unknown }) {
  const [sectionFilter, setSectionFilter] = useState<TargetSectionCode | 'All'>('All')
  const filteredTargets = sectionFilter === 'All'
    ? targets
    : targets.filter((target) => targetMatchesSection(target.section, sectionFilter))

  return (
    <MimCard title="Website Targets" subtitle="Exact URLs, publishing organisations, dossier sections, processing outcomes, and evidence completeness.">
      <div className="run-section-filters" role="group" aria-label="Filter targets by dossier section">
        {TARGET_SECTION_FILTER_CHIPS.map((chip) => (
          <button
            key={chip}
            type="button"
            className={sectionFilter === chip ? 'active' : ''}
            onClick={() => setSectionFilter(chip)}
          >
            {targetSectionFilterLabel(chip)}
          </button>
        ))}
      </div>
      {error
        ? <QueryFailure label="Website targets could not be loaded" error={error} />
        : <TargetsTable targets={filteredTargets} loading={loading} showSection />}
    </MimCard>
  )
}

function TargetsTable({ targets, loading, compact = false, showSection = false }: { targets: ComplianceTargetRunDto[]; loading: boolean; compact?: boolean; showSection?: boolean }) {
  if (loading) return <LoadingRows label="Loading targets" />
  if (targets.length === 0) return <EmptyState title="No target ledger entries" detail="The run has not persisted a target record." />
  return (
    <div className="mim-table-wrap">
      <table className="mim-table">
        <thead><tr><th>Website source</th>{showSection && <th>Section</th>}<th>Publisher</th><th>Stage</th><th>Outcome</th>{!compact && <><th>Fields</th><th>Findings</th><th>Evidence</th></>}</tr></thead>
        <tbody>
          {targets.map((target) => (
            <tr key={target.id}>
              <td>
                <a className="run-source-link" href={target.finalUrl || target.url} target="_blank" rel="noreferrer">
                  {target.finalUrl || target.url} <ExternalLink size={12} />
                </a>
                {target.errorMessage && <span className="run-row-error">{target.errorMessage}</span>}
              </td>
              {showSection && <td><StatusPill status={target.sectionLabel || targetSectionLabel(target.section)} tone="info" /></td>}
              <td><strong>{target.publisherName}</strong><span className="run-cell-sub">{publisherTypeLabel(target.publisherType)}</span></td>
              <td><StatusPill status={stageLabel(target.stage)} /></td>
              <td><StatusPill status={target.outcome || (activeStatuses.has(target.stage) ? 'In progress' : 'Pending')} /></td>
              {!compact && <>
                <td>{target.extractionCount} extracted / {target.comparisonCount} compared</td>
                <td>{target.findingCount}</td>
                <td><StatusPill status={target.evidenceComplete ? `${target.evidenceCount} verified` : `${target.evidenceCount} incomplete`} tone={target.evidenceComplete ? 'pass' : 'warning'} /></td>
              </>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RunDataTab({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return <MimCard title={title} subtitle={subtitle}><div className="mim-stack">{children}</div></MimCard>
}

const ALL_TARGETS = '__all__'

function TargetPicker({ targets, value, onChange, allowAll = false }: {
  targets: ComplianceTargetRunDto[]
  value: string
  onChange: (value: string) => void
  allowAll?: boolean
}) {
  return (
    <label className="run-target-picker">
      <span className="mim-label">Website target</span>
      <select className="mim-select" value={value} onChange={(event) => onChange(event.target.value)} disabled={targets.length === 0}>
        {targets.length === 0 && <option value="">No targets available</option>}
        {allowAll && targets.length > 0 && <option value={ALL_TARGETS}>All targets ({targets.length} websites, aggregated)</option>}
        {targets.map((target) => (
          <option key={target.id} value={target.id}>
            {targetSectionLabel(target.section)} · {target.publisherName} — {target.finalUrl || target.url}
          </option>
        ))}
      </select>
    </label>
  )
}

function ExtractionsTable({ rows, loading }: { rows: Awaited<ReturnType<typeof fetchComplianceRunExtractions>>; loading: boolean }) {
  if (loading) return <LoadingRows label="Loading extracted fields" />
  if (rows.length === 0) return <EmptyState title="No extracted fields" detail="Extraction has not completed for this target, or no candidates were found." />
  return (
    <div className="mim-table-wrap">
      <table className="mim-table run-data-table">
        <thead><tr><th>Field</th><th>PCR expects</th><th>Disposition</th><th>Observed value</th><th>Confidence</th><th>Evidence</th><th>Method</th></tr></thead>
        <tbody>{rows.map((row) => {
          const disposition = dispositionDisplay(row)
          return (
            <tr key={row.id}>
              <td><strong>{row.displayLabel || row.canonicalField}</strong><span className="run-cell-sub mim-mono">{row.canonicalField}</span></td>
              <td><span className="run-value">{row.expectedValue ?? 'Missing in PCR'}</span>{row.expectedUnit && <span className="run-cell-sub">{row.expectedUnit}</span>}{row.expectedRequiredOnWebsite && <span className="run-cell-sub">Required on website</span>}</td>
              <td><StatusPill status={disposition.label} tone={disposition.tone} />{row.mappingRationale && <span className="run-cell-sub" title={row.mappingRationale}>{row.mappingRationale}</span>}</td>
              <td><span className="run-value">{row.normalizedValue || row.rawValue || 'No value'}</span>{row.unit && <span className="run-cell-sub">Unit: {row.unit}</span>}</td>
              <td>{formatConfidence(row.confidence)}</td>
              <td><EvidenceQuote quote={row.evidenceQuote} sourceUrl={row.sourceUrl} location={row.evidenceLocation} /></td>
              <td>{row.extractionMethod}<span className="run-cell-sub">{[row.model, row.promptVersion].filter(Boolean).join(' · ')}</span></td>
            </tr>
          )
        })}</tbody>
      </table>
    </div>
  )
}

function ComparisonsPanel({ rows, runId, targetRunId, loading }: { rows: ComplianceComparisonDto[]; runId: string; targetRunId: string; loading: boolean }) {
  const queryClient = useQueryClient()
  const [reviewingId, setReviewingId] = useState<string | null>(null)
  const [decision, setDecision] = useState<'Match' | 'Mismatch' | 'NeedsReview'>('NeedsReview')
  const [rationale, setRationale] = useState('')
  const decisionMutation = useMutation({
    mutationFn: ({ comparisonId, nextDecision, nextRationale }: { comparisonId: string; nextDecision: typeof decision; nextRationale: string }) =>
      createComplianceReviewDecision(comparisonId, { decision: nextDecision, rationale: nextRationale }),
    onSuccess: () => {
      setReviewingId(null)
      setRationale('')
      void queryClient.invalidateQueries({ queryKey: ['compliance-run-comparisons', runId, targetRunId] })
    },
  })

  if (loading) return <LoadingRows label="Loading comparison matrix" />
  if (rows.length === 0) return <EmptyState title="No PCR comparisons" detail="No field comparisons are stored for this target. A blocked page capture skips comparison; otherwise this stage has not finished." />

  const needsReviewRows = rows.filter((row) => (row.latestDecision?.decision || row.outcome) === 'NeedsReview')
  const grouped = Array.from(rows.reduce((map, row) => {
    const key = row.canonicalField
    const bucket = map.get(key) ?? []
    bucket.push(row)
    map.set(key, bucket)
    return map
  }, new Map<string, ComplianceComparisonDto[]>()).entries())

  const submitQuickDecision = (comparisonId: string, nextDecision: 'Match' | 'Mismatch') => {
    const nextRationale = nextDecision === 'Match'
      ? 'Reviewer accepted the website value as matching PCR intent.'
      : 'Reviewer overrode as mismatch after manual evidence review.'
    decisionMutation.mutate({ comparisonId, nextDecision, nextRationale })
  }

  return (
    <div className="run-comparisons-layout">
      <div className="run-comparisons-main">
        <div className="mim-table-wrap">
          <table className="mim-table run-data-table run-comparison-table">
            <thead><tr><th>Field</th><th>Variants</th><th>PCR expected</th><th>Website observed</th><th>Outcome</th><th>Evidence / decision</th></tr></thead>
            <tbody>{grouped.map(([canonicalField, fieldRows]) => {
              // Represent the field by a row that actually observed something - previously
              // fieldRows[0] could be an unobserved variant, making the summary claim
              // "Not observed" while other variants plainly matched on the page.
              const primary =
                fieldRows.find((row) => (row.latestDecision?.decision || row.outcome) === 'Match' && row.observedValue)
                ?? fieldRows.find((row) => row.observedValue)
                ?? fieldRows[0]
              return (
                <tr key={canonicalField}>
                  <td>
                    <strong>{primary.displayLabel || primary.canonicalField}</strong>
                    <span className="run-cell-sub">{primary.requiredOnWebsite ? 'Required on website' : 'Optional on website'}</span>
                  </td>
                  <td><VariantOutcomeChips rows={fieldRows} /></td>
                  <td><span className="run-value">{primary.expectedValue ?? 'Missing in PCR'}</span>{primary.expectedUnit && <span className="run-cell-sub">{primary.expectedUnit}</span>}</td>
                  <td><span className="run-value">{primary.observedValue ?? 'Not observed'}</span>{primary.observedUnit && <span className="run-cell-sub">{primary.observedUnit}</span>}</td>
                  <td>
                    <StatusPill status={primary.latestDecision?.decision || primary.outcome} />
                    {primary.severity && <span className="run-cell-sub">{primary.severity}{primary.clause ? ` · ${primary.clause}` : ''}</span>}
                  </td>
                  <td>
                    {fieldRows.map((row) => (
                      <div key={row.id} className="run-comparison-variant-row">
                        {row.variantLabel && <span className="run-variant-chip-label">{row.variantLabel}</span>}
                        {fieldRows.length > 1 && (
                          <span className="run-cell-sub">
                            {row.expectedValue ?? 'Missing in PCR'} → {row.observedValue ?? 'Not observed'} · {row.latestDecision?.decision || row.outcome}
                          </span>
                        )}
                        <EvidenceQuote quote={row.evidenceQuote} sourceUrl={row.sourceUrl} />
                        {row.latestDecision && <div className="run-review-summary"><strong>Human: {row.latestDecision.decision}</strong><span>{row.latestDecision.rationale}</span></div>}
                        {reviewingId === row.id ? (
                          <div className="run-review-form">
                            <select className="mim-select" value={decision} onChange={(event) => setDecision(event.target.value as typeof decision)}>
                              <option value="Match">Match</option><option value="Mismatch">Mismatch</option><option value="NeedsReview">Needs review</option>
                            </select>
                            <textarea className="mim-input" rows={3} placeholder="Required rationale" value={rationale} onChange={(event) => setRationale(event.target.value)} />
                            <button type="button" className="mim-btn mim-btn-primary" disabled={!rationale.trim() || decisionMutation.isPending} onClick={() => decisionMutation.mutate({ comparisonId: row.id, nextDecision: decision, nextRationale: rationale.trim() })}>
                              Record decision
                            </button>
                          </div>
                        ) : (
                          <button type="button" className="mim-btn mim-btn-secondary run-review-button" onClick={() => { setReviewingId(row.id); setDecision('NeedsReview'); setRationale('') }}>Review</button>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
              )
            })}</tbody>
          </table>
        </div>
      </div>

      <aside className="run-needs-review-panel">
        <h3>Needs review ({needsReviewRows.length})</h3>
        <p className="mim-help-text">Side-by-side evidence for ambiguous comparisons. Accept as match or override as mismatch.</p>
        {needsReviewRows.length === 0
          ? <EmptyState title="Nothing awaiting review" detail="All comparisons on this target are resolved or passed automatically." />
          : needsReviewRows.map((row) => (
            <article key={row.id} className="run-needs-review-card">
              <strong>{row.displayLabel || row.canonicalField}</strong>
              {row.variantLabel && <span className="run-variant-chip-label">{row.variantLabel}</span>}
              <div className="run-needs-review-side-by-side">
                <div><span className="mim-label">PCR shows</span><span className="run-value">{row.expectedValue ?? 'Missing'}{row.expectedUnit ? ` ${row.expectedUnit}` : ''}</span></div>
                <div><span className="mim-label">Website states</span><span className="run-value">{row.observedValue ?? 'Not observed'}{row.observedUnit ? ` ${row.observedUnit}` : ''}</span></div>
              </div>
              <EvidenceQuote quote={row.evidenceQuote} sourceUrl={row.sourceUrl} />
              <div className="mim-toolbar">
                <button type="button" className="mim-btn mim-btn-secondary" disabled={decisionMutation.isPending} onClick={() => submitQuickDecision(row.id, 'Match')}>Accept</button>
                <button type="button" className="mim-btn mim-btn-danger" disabled={decisionMutation.isPending} onClick={() => submitQuickDecision(row.id, 'Mismatch')}>Override</button>
              </div>
            </article>
          ))}
      </aside>
    </div>
  )
}

function VariantOutcomeChips({ rows }: { rows: ComplianceComparisonDto[] }) {
  const variants = rows.some((row) => row.variantLabel)
    ? rows
    : [{ ...rows[0], variantLabel: 'default' }]
  return (
    <div className="run-variant-chip-row">
      {variants.map((row) => {
        const outcome = row.latestDecision?.decision || row.outcome
        const pass = outcome === 'Match'
        const fail = outcome === 'Mismatch' || outcome === 'MissingObserved' || outcome === 'TechnicalError'
        const symbol = pass ? '✓' : fail ? '✗' : '?'
        const label = row.variantLabel || row.canonicalField
        return (
          <span key={row.id} className={`run-variant-chip ${pass ? 'pass' : fail ? 'fail' : 'review'}`} title={`${label}: ${outcome}`}>
            {label}: {symbol}
          </span>
        )
      })}
    </div>
  )
}

function RuleResultsTable({ rows, loading, showSource = false }: {
  rows: Awaited<ReturnType<typeof fetchComplianceRunRuleResults>>
  loading: boolean
  showSource?: boolean
}) {
  if (loading) return <LoadingRows label="Loading policy results" />

  // FieldComparison rows are auto-generated one-per-PCR-field to drive the findings pipeline.
  // Only the "NotApplicable" ones are pure noise here - an optional field with nothing found is
  // not a finding and is already visible on PCR Comparison. A field that actually Passed or Failed
  // is a genuine result (the same field mismatch that produced a Finding) and must stay visible,
  // same as a real rule pack's restricted-term/broken-link checks.
  const policyRows = rows.filter((row) => !(row.ruleType === 'FieldComparison' && row.outcome === 'NotApplicable'))
  if (policyRows.length === 0) {
    return <EmptyState title="No policy rule results" detail="No optional rule pack was selected, and every core field comparison was either a match or not applicable." />
  }

  const passed = policyRows.filter((row) => row.outcome === 'Pass' || row.outcome === 'Passed')
  const failed = policyRows.filter((row) => row.outcome === 'Fail' || row.outcome === 'Failed')
  const other = policyRows.filter((row) => !passed.includes(row) && !failed.includes(row))

  return (
    <div className="mim-stack">
      <RuleResultGroup title="Failed" tone="critical" rows={failed} emptyDetail="No rule pack checks failed." showSource={showSource} />
      <RuleResultGroup title="Passed" tone="pass" rows={passed} emptyDetail="No rule pack checks passed." showSource={showSource} />
      {other.length > 0 && (
        <RuleResultGroup title="Needs review / not applicable" tone="neutral" rows={other} emptyDetail="" collapsedByDefault showSource={showSource} />
      )}
    </div>
  )
}

function RuleResultGroup({
  title,
  tone,
  rows,
  emptyDetail,
  collapsedByDefault,
  showSource = false,
}: {
  title: string
  tone: 'critical' | 'pass' | 'neutral'
  rows: Awaited<ReturnType<typeof fetchComplianceRunRuleResults>>
  emptyDetail: string
  collapsedByDefault?: boolean
  showSource?: boolean
}) {
  const [collapsed, setCollapsed] = useState(!!collapsedByDefault)
  if (rows.length === 0 && !emptyDetail) return null
  const borderColor = tone === 'critical' ? '#dc2626' : tone === 'pass' ? '#16a34a' : '#9ca3af'
  return (
    <div className="mim-card" style={{ borderTop: `3px solid ${borderColor}` }}>
      <div className="mim-card-header" style={{ cursor: 'pointer' }} onClick={() => setCollapsed((c) => !c)}>
        <div>
          <h2>{title} ({rows.length})</h2>
        </div>
        <button type="button" className="mim-btn mim-btn-secondary">{collapsed ? 'Show' : 'Hide'}</button>
      </div>
      {!collapsed && (
        <div className="mim-card-body">
          {rows.length === 0 ? (
            <EmptyState title={emptyDetail} />
          ) : (
            <div className="mim-table-wrap"><table className="mim-table run-data-table">
              <thead><tr><th>Rule</th><th>Type</th>{showSource && <th>Website</th>}<th>PCR expects</th><th>Website shows</th><th>Severity / clause</th><th>Result and evidence</th></tr></thead>
              <tbody>{rows.map((row) => <tr key={row.id}>
                <td><strong>{row.ruleName}</strong><span className="run-cell-sub mim-mono">{row.ruleKey}</span></td>
                <td>{ruleTypeLabel(row.ruleType)}</td>
                {showSource && (
                  <td>{row.sourceUrl
                    ? <a href={row.sourceUrl} target="_blank" rel="noreferrer" className="run-cell-sub">{shortUrlLabel(row.sourceUrl)}</a>
                    : '-'}</td>
                )}
                <td>{row.expectedValue ? <span className="run-value">{row.expectedValue}{row.expectedUnit ? ` ${row.expectedUnit}` : ''}</span> : '-'}</td>
                <td>{row.observedValue ? <span className="run-value">{row.observedValue}{row.observedUnit ? ` ${row.observedUnit}` : ''}</span> : '-'}</td>
                <td>{row.severity || '-'}<span className="run-cell-sub">{row.clause}</span></td>
                <td>{row.message || '-'}<EvidenceQuote quote={row.evidenceQuote} sourceUrl={row.sourceUrl} /></td>
              </tr>)}</tbody>
            </table></div>
          )}
        </div>
      )}
    </div>
  )
}

function shortUrlLabel(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop()
    return lastSegment || parsed.hostname
  } catch {
    return url
  }
}

function EvidenceAuditTab({ run, target, evidence, loading, error, retry }: { run: ComplianceRunDetailDto; target?: ComplianceTargetRunDto; evidence: EvidenceMetadataDto[]; loading: boolean; error: unknown; retry: () => unknown }) {
  const [artifactError, setArtifactError] = useState<string | null>(null)
  const artifactMutation = useMutation({
    mutationFn: async ({ artifact, preview }: { artifact: EvidenceMetadataDto; preview: boolean }) => {
      const blob = await downloadEvidenceContent(artifact.artifactId)
      const objectUrl = URL.createObjectURL(blob)
      if (preview) {
        window.open(objectUrl, '_blank', 'noopener,noreferrer')
      } else {
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = evidenceFileName(artifact)
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000)
    },
    onMutate: () => setArtifactError(null),
    onError: (error) => setArtifactError(apiError(error)),
  })

  return (
    <div className="mim-stack">
      <div className="run-overview-grid">
        <MimCard title="Evidence Manifest" subtitle="Artifacts are fetched through the authenticated API; storage addresses are never exposed.">
          {error ? <QueryFailure label="Evidence manifest could not be loaded" error={error} retry={retry} /> : loading ? <LoadingRows label="Loading evidence manifest" /> : evidence.length === 0 ? <EmptyState title="No evidence artifacts" detail="The acquisition stage has not stored evidence for this run." /> : (
            <div className="run-evidence-list">
              {evidence.map((artifact) => (
                <article key={artifact.artifactId} className="run-evidence-row">
                  <FileSearch size={18} />
                  <div>
                    <strong>{artifact.artifactType}</strong>
                    <span>{formatBytes(artifact.contentLength)} · {artifact.contentType || 'Unknown content type'} · {formatDateTime(artifact.createdUtc)}</span>
                    <code>SHA-256 {artifact.sha256}</code>
                  </div>
                  <StatusPill status={artifact.integrityStatus} tone={artifact.integrityStatus === 'Verified' ? 'pass' : 'critical'} />
                  <div className="mim-toolbar">
                    {isPreviewable(artifact.contentType) && <button type="button" className="mim-icon-button" title="Authenticated preview" disabled={artifactMutation.isPending} onClick={() => artifactMutation.mutate({ artifact, preview: true })}><ExternalLink size={14} /></button>}
                    <button type="button" className="mim-icon-button" title="Authenticated download" disabled={artifactMutation.isPending} onClick={() => artifactMutation.mutate({ artifact, preview: false })}><Download size={14} /></button>
                  </div>
                </article>
              ))}
            </div>
          )}
          {artifactError && <div className="run-row-error">{artifactError}</div>}
        </MimCard>
        <MimCard title="Audit and Versions">
          <dl className="run-definition-list">
            <Definition label="Run ID" value={run.id} mono />
            <Definition label="PCR ID" value={run.sourceOfTruthRecordId} mono />
            <Definition label="PCR hash" value={run.sourceOfTruth?.hash || run.sourceOfTruthHash || 'Unavailable'} mono />
            <Definition label="Extraction profile" value={revisionLabel(run.extractionProfile)} mono />
            <Definition label="Extraction profile scope" value={run.extractionProfileMatchedScope || 'Not reported - profile may have been re-scoped since this run'} />
            <Definition label="Rule pack" value={revisionLabel(run.rulePack, 'Core only')} mono />
            <Definition label="Prompt pack" value={revisionLabel(run.promptPack, 'Not used')} mono />
            <Definition label="Algorithm" value={run.comparisonAlgorithmVersion || 'Unavailable'} mono />
            <Definition label="Run started" value={formatDateTime(run.startedUtc) || 'Unavailable'} />
            <Definition label="Run completed" value={formatDateTime(run.completedUtc) || 'Unavailable'} />
            <Definition label="Run by" value={run.initiatedBy || 'Unavailable'} />
            <Definition label="Trigger" value={run.reason || 'Manual website compliance check'} />
            <Definition label="Application" value={run.applicationVersion || 'Unavailable'} mono />
            <Definition label="Selected target" value={target?.url || 'All run evidence'} />
          </dl>
        </MimCard>
      </div>
    </div>
  )
}

function LegacyRunDetail({ run, checkRunId }: { run: CheckRunDetailDto; checkRunId: string }) {
  const queryClient = useQueryClient()
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const progressQuery = useQuery({
    queryKey: ['legacy-check-run-progress', checkRunId],
    queryFn: () => fetchCheckRunProgressLog(checkRunId),
    refetchInterval: activeStatuses.has(run.status) ? 2500 : false,
  })
  const cancelMutation = useMutation({
    mutationFn: () => cancelCheckRun(checkRunId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-run-detail', checkRunId] })
      void queryClient.invalidateQueries({ queryKey: ['check-runs'] })
    },
  })

  const exportReport = async () => {
    setExportError(null)
    setExporting(true)
    try {
      const blob = await downloadCheckRunReportPdf(checkRunId)
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `legacy-check-run-${checkRunId}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (error) {
      setExportError(apiError(error))
    } finally {
      setExporting(false)
    }
  }

  return (
    <MimPage
      title="Legacy Check Run"
      subtitle={checkRunId}
      actions={<Link className="mim-btn mim-btn-secondary" to="/check-runs"><ArrowLeft size={14} /> History</Link>}
    >
      <div className="mim-stack">
        <div className="run-legacy-banner">
          <FileClock size={26} />
          <div><strong>Legacy / Non-replayable</strong><span>This result predates the immutable run ledger. It is available for operational reference only and must not be presented as an audited compliance decision.</span></div>
        </div>
        <div className="mim-grid-4">
          <KpiCard label="Status" value={<StatusPill status={run.status} />} />
          <KpiCard label="Targets" value={run.targetCount} />
          <KpiCard label="Processed" value={run.processedCount} tone="grey" />
          <KpiCard label="Findings" value={run.findingCount} tone="red" />
        </div>
        <div className="run-overview-grid">
          <MimCard title="Legacy Run Metadata">
            <dl className="run-definition-list">
              <Definition label="Started" value={formatDateTime(run.startedUtc)} />
              <Definition label="Completed" value={formatDateTime(run.completedUtc)} />
              <Definition label="Reason" value={run.reason || '-'} />
              <Definition label="Root URL" value={run.rootUrl || '-'} mono />
              <Definition label="Error" value={run.errorMessage || '-'} />
            </dl>
            <div className="mim-toolbar run-legacy-actions">
              <button type="button" className="mim-btn mim-btn-secondary" disabled={exporting} onClick={exportReport}><Download size={14} /> {exporting ? 'Exporting…' : 'Export legacy PDF'}</button>
              {activeStatuses.has(run.status) && <button type="button" className="mim-btn mim-btn-danger" disabled={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>Stop run</button>}
            </div>
            {exportError && <span className="run-row-error">{exportError}</span>}
          </MimCard>
          <MimCard title="Progress Log">
            {progressQuery.isLoading ? <LoadingRows label="Loading progress log" /> : (
              <ol className="run-progress-log">
                {(progressQuery.data ?? []).map((entry, index) => <li key={`${entry.timestampUtc}-${index}`}><time>{formatDateTime(entry.timestampUtc)}</time><strong>{entry.step || entry.level || 'Update'}</strong><span>{entry.message}</span></li>)}
                {(progressQuery.data ?? []).length === 0 && <li><span>No progress entries were recorded.</span></li>}
              </ol>
            )}
          </MimCard>
        </div>
        <MimCard title="Legacy Findings">
          {run.findings.length === 0 ? <EmptyState title="No legacy findings recorded" detail="Absence of findings is not proof of compliance for a non-replayable run." /> : (
            <div className="mim-table-wrap"><table className="mim-table"><thead><tr><th>Severity</th><th>Status</th><th>Description</th><th>Expected</th><th>Observed</th><th>Clause</th></tr></thead><tbody>
              {run.findings.map((finding) => <tr key={finding.id}><td><StatusPill status={finding.severity} /></td><td><StatusPill status={finding.status} /></td><td>{finding.description}</td><td>{finding.expectedValue || '-'}</td><td>{finding.observedValue || '-'}</td><td>{finding.mimClauseMappings || '-'}</td></tr>)}
            </tbody></table></div>
          )}
        </MimCard>
      </div>
    </MimPage>
  )
}

function Definition({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return <div><dt>{label}</dt><dd className={mono ? 'mim-mono' : ''}>{value}</dd></div>
}

function EvidenceQuote({ quote, sourceUrl, location }: { quote?: string; sourceUrl?: string; location?: string }) {
  if (!quote && !sourceUrl) return <span className="run-cell-sub">No quote recorded</span>
  return (
    <div className="run-evidence-quote">
      {quote && <q>{quote}</q>}
      {location && <span>{location}</span>}
      {sourceUrl && <a href={sourceUrl} target="_blank" rel="noreferrer">Captured source <ExternalLink size={11} /></a>}
    </div>
  )
}

function LoadingRows({ label }: { label: string }) {
  return <div className="run-loading"><LoaderCircle className="run-spin" size={18} /> {label}</div>
}

function QueryFailure({ label, error, retry }: { label: string; error: unknown; retry?: () => unknown }) {
  return (
    <div className="run-query-error">
      <AlertTriangle size={20} />
      <div><strong>{label}</strong><span>{apiError(error)}</span></div>
      {retry && <button type="button" className="mim-btn mim-btn-secondary" onClick={() => retry()}><RefreshCw size={13} /> Retry</button>}
    </div>
  )
}

function ruleTypeLabel(ruleType: string): string {
  if (ruleType === 'FieldComparison') return 'PCR Field Check'
  if (ruleType === 'RestrictedTerm') return 'Restricted Term'
  if (ruleType === 'UnsupportedClaim') return 'Unsubstantiated Claim'
  if (ruleType === 'BrokenLink') return 'Broken Link'
  if (ruleType === 'RequiredClaim') return 'Required Reference'
  if (ruleType === 'FieldRule') return 'Field Rule'
  return ruleType
}

function dispositionDisplay(row: { disposition: string; normalizedValue?: string; rawValue?: string }): {
  label: string
  tone: 'pass' | 'critical' | 'warning' | 'neutral' | 'info'
} {
  const hasValue = !!(row.normalizedValue || row.rawValue)
  if (!hasValue) return { label: 'Nothing found on page', tone: 'info' }
  switch (row.disposition) {
    case 'Accepted': return { label: 'Data found - Accepted', tone: 'pass' }
    case 'Rejected': return { label: 'Data found - Rejected', tone: 'critical' }
    case 'Ambiguous': return { label: 'Data found - Ambiguous', tone: 'warning' }
    case 'Irrelevant': return { label: 'Data found - Irrelevant', tone: 'neutral' }
    default: return { label: row.disposition, tone: 'neutral' }
  }
}

function variantCoverageLabel(coveredVariants?: { variantLabel: string }[]) {
  if (!coveredVariants || coveredVariants.length <= 1) {
    return 'Single approved variant PCR (no sibling variants existed at run time)'
  }
  const labels = coveredVariants.map((v) => v.variantLabel)
  const shown = labels.slice(0, 5).join(', ')
  const more = labels.length > 5 ? `, +${labels.length - 5} more` : ''
  return `Checked against ${coveredVariants.length} approved variants (${shown}${more})`
}

function revisionLabel(revision?: { id?: string; name?: string; version?: string; hash?: string }, fallback = 'Not reported') {
  if (!revision) return fallback
  const label = [revision.name, revision.version ? `v${revision.version}` : undefined].filter(Boolean).join(' ')
  return [label || revision.id, revision.hash].filter(Boolean).join(' · ') || fallback
}

function publisherTypeLabel(type: ComplianceTargetRunDto['publisherType']) {
  if (type === 'InternalOwned') return 'Internal Owned'
  if (type === 'AccreditedDistributor') return 'Accredited Distributor'
  return 'Independent Supplier'
}

function stageLabel(stage: ComplianceTargetRunDto['stage']) {
  if (stage === 'EvaluatingRules') return 'Evaluating rules'
  return stage
}

function formatConfidence(value?: number) {
  if (value === undefined || value === null) return '-'
  return `${Math.round((value <= 1 ? value * 100 : value))}%`
}

function formatBytes(value?: number) {
  if (value === undefined || value === null) return 'Size unavailable'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function isPreviewable(contentType?: string) {
  return !!contentType && (contentType.startsWith('image/') || contentType.startsWith('text/') || contentType === 'application/pdf' || contentType.includes('json'))
}

function evidenceFileName(artifact: EvidenceMetadataDto) {
  const extension = artifact.contentType?.includes('json') ? 'json'
    : artifact.contentType?.includes('html') ? 'html'
      : artifact.contentType?.includes('markdown') || artifact.contentType?.startsWith('text/') ? 'txt'
        : artifact.contentType === 'application/pdf' ? 'pdf'
          : artifact.contentType?.startsWith('image/') ? artifact.contentType.split('/')[1] : 'bin'
  return `${artifact.artifactType}-${artifact.artifactId}.${extension}`
}

function apiError(error: unknown) {
  const response = (error as { response?: { data?: unknown; status?: number } })?.response
  const data = response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const candidate = data as { detail?: unknown; message?: unknown; title?: unknown }
    if (typeof candidate.detail === 'string') return candidate.detail
    if (typeof candidate.message === 'string') return candidate.message
    if (typeof candidate.title === 'string') return candidate.title
  }
  return error instanceof Error ? error.message : `The service did not return the requested run data${response?.status ? ` (HTTP ${response.status})` : ''}.`
}
